import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const fmt$ = (n) => `¥${Number(n || 0).toFixed(2)}`;
const when = (s) => (s ? s.slice(0, 16).replace("T", " ") : "—");

// Withdrawn refund requests — kept (not deleted) so a mis-click can be undone.
export function WithdrawnRefundsSection({ refunds, onRestore, busy }) {
  const [open, setOpen] = useState(false);
  if (!refunds.length) return null;
  return (
    <section className="bg-slate-50 border border-slate-200 rounded-md overflow-hidden" data-testid="withdrawn-refunds-section">
      <button type="button" className="w-full px-5 py-3 flex items-center justify-between text-left" onClick={() => setOpen((v) => !v)} data-testid="withdrawn-refunds-toggle">
        <div className="flex items-baseline gap-3">
          <div className="text-[10px] uppercase tracking-widest font-mono opacity-70 text-slate-700">Withdrawn refund requests</div>
          <div className="font-display text-lg font-bold text-slate-800">{refunds.length} withdrawn</div>
          <span className="text-xs text-slate-500">— pressed Withdraw by mistake? Restore puts it back in the pending list.</span>
        </div>
        <span className="text-slate-400 text-xs">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <table className="w-full text-sm border-t border-slate-200">
          <tbody>
            {refunds.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`withdrawn-refund-${r.id}`}>
                <td className="px-4 py-2 font-mono font-semibold">{r.po_number || "—"}</td>
                <td className="px-4 py-2 font-mono text-red-700">−{fmt$(r.amount)}</td>
                <td className="px-4 py-2 text-xs text-slate-600 max-w-xs truncate" title={r.reason}>{r.reason || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-500">withdrawn {when(r.withdrawn_at)} by {r.withdrawn_by || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" disabled={busy} onClick={() => onRestore(r.id)} data-testid={`restore-refund-${r.id}`}>
                    ↩ Restore to pending
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Finds billed + cancelled / claim-refunded orders whose refund request no longer exists and re-creates them.
export function LostRefundsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [amounts, setAmounts] = useState({});
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["refunds-lost"],
    queryFn: async () => (await api.get("/refunds/lost")).data,
    enabled: open,
  });
  const recover = useMutation({
    mutationFn: (c) => api.post("/refunds/recover", {
      order_id: c.order_id, amount: parseFloat(amounts[c.order_id] ?? c.amount), reason: c.reason,
      requested_at: c.requested_at, requested_by: c.requested_by, claim_id: c.claim_id || null,
    }),
    onSuccess: (res) => { toast.success(`Refund request for ${res.data.po_number} is back in the pending list`); qc.invalidateQueries({ queryKey: ["refunds"] }); refetch(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not re-create the request"),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="lost-refunds-dialog">
        <DialogHeader>
          <DialogTitle>Find lost refund requests</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">
          Orders that were billed to the factory and then cancelled or refunded through a claim, but have <span className="font-semibold">no refund request on file</span> — usually one that was withdrawn by mistake. Re-create keeps the original date and requester, and the request goes back to Factory 1 for approval.
        </p>
        {isLoading ? <div className="text-sm text-slate-500 py-6 text-center">Scanning orders…</div> : data.length === 0 ? (
          <div className="text-sm text-emerald-700 py-6 text-center" data-testid="lost-refunds-empty">Every billed cancellation has its refund request — nothing is missing.</div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>{["PO #", "Factory", "Amount", "Reason", "Requested", "", ""].map((h, i) => <th key={i} className="px-3 py-2 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{h}</th>)}</tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.order_id} className="border-t border-slate-100 align-top" data-testid={`lost-refund-${c.order_id}`}>
                    <td className="px-3 py-2 font-mono font-semibold">{c.po_number}<div className="text-[10px] font-sans font-normal text-slate-500">{c.vehicle}</div></td>
                    <td className="px-3 py-2"><Badge className="border bg-red-50 border-red-200 text-red-800">{c.factory === "factory_2" ? "Factory 2" : "Factory 1"}</Badge></td>
                    <td className="px-3 py-2">
                      <Input type="number" step="0.01" min="0" className="h-8 w-28 font-mono" value={amounts[c.order_id] ?? c.amount} onChange={(e) => setAmounts((a) => ({ ...a, [c.order_id]: e.target.value }))} data-testid={`lost-refund-amount-${c.order_id}`} />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 max-w-xs" title={c.reason}>{c.reason}<div className="text-[10px] text-slate-400 mt-0.5">{c.source === "claim" ? "from an after-sale claim" : c.evidence}</div></td>
                    <td className="px-3 py-2 text-xs text-slate-500 font-mono whitespace-nowrap">{when(c.requested_at)}<div className="font-sans">{c.requested_by || "—"}</div></td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={recover.isPending || !(parseFloat(amounts[c.order_id] ?? c.amount) > 0)} onClick={() => recover.mutate(c)} data-testid={`recover-refund-${c.order_id}`}>
                        Re-create request
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
